import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { NativeGeocoder } from '@capgo/nativegeocoder';
import { ParadasService } from './paradas.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private paradas_cercanas: any[] = [];
  private geoAddress: string = '';
  private geoLatitude: number = 0;
  private geoLongitude: number = 0;
  private lastFetchTime: number = 0;
  private CACHE_DURATION = 2 * 60 * 1000; // 2 minutos en milisegundos

  private geoencoderOptions = {
    useLocale: true,
    maxResults: 5
  };

  // BehaviorSubject para notificar cambios
  private paradasCercanasSubject = new BehaviorSubject<any[]>([]);
  public paradasCercanas$ = this.paradasCercanasSubject.asObservable();

  private geoAddressSubject = new BehaviorSubject<string>('');
  public geoAddress$ = this.geoAddressSubject.asObservable();

  constructor(private paradasService: ParadasService) {}

  async getParadasCercanas(amount: number = 5): Promise<any[]> {
    const now = Date.now();

    // Si tenemos datos en cache y no han pasado 2 minutos, devolver cache
    if (this.paradas_cercanas.length > 0 && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.paradas_cercanas;
    }

    try {
      // Obtener nueva geolocalización
      const position = await Geolocation.getCurrentPosition();
      this.geoLatitude = position.coords.latitude;
      this.geoLongitude = position.coords.longitude;

      // Obtener dirección
      await this.getGeoencoder(this.geoLatitude, this.geoLongitude);

      // Obtener paradas cercanas
      return new Promise((resolve, reject) => {
        this.paradasService.getNearestParadas({
          lat: this.geoLatitude,
          lon: this.geoLongitude
        }, amount).subscribe({
          next: (resp) => {
            resp.forEach((parada: any) => {
              parada.parada = parseInt(parada.parada);
              parada.distance = parseFloat(parada.distance);
            });

            this.paradas_cercanas = resp;
            this.lastFetchTime = now;

            // Notificar a los subscriptores
            this.paradasCercanasSubject.next(this.paradas_cercanas);

            resolve(this.paradas_cercanas);
          },
          error: (error) => {
            console.error('Error obteniendo paradas cercanas:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.log('Error getting location: ' + JSON.stringify(error));
      throw error;
    }
  }

  private async getGeoencoder(latitude: number, longitude: number) {
    try {
      const result = await NativeGeocoder.reverseGeocode({
        latitude: latitude,
        longitude: longitude,
        useLocale: this.geoencoderOptions.useLocale,
        maxResults: this.geoencoderOptions.maxResults
      });

      if (result.addresses && result.addresses.length > 0) {
        this.geoAddress = this.generateAddress(result.addresses[0]);
        this.geoAddressSubject.next(this.geoAddress);
      }
    } catch (error) {
      console.log('Error getting location: ' + JSON.stringify(error));
    }
  }

  private generateAddress(addressObj: any): string {
    let obj = [];
    let address = "";
    for (let key in addressObj) {
      obj.push(addressObj[key]);
    }
    obj.reverse();
    for (let val in obj) {
      if (obj[val].length) {
        address += obj[val] + ', ';
      }
    }
    return address.slice(0, -2);
  }

  // Método para obtener las paradas cercanas actuales sin hacer nueva petición
  getCurrentParadasCercanas(): any[] {
    return this.paradas_cercanas;
  }

  // Método para obtener la dirección actual
  getCurrentGeoAddress(): string {
    return this.geoAddress;
  }

  // Método para limpiar el cache manualmente
  clearCache(): void {
    this.paradas_cercanas = [];
    this.lastFetchTime = 0;
    this.paradasCercanasSubject.next([]);
  }
}